/**
 * @fileoverview Mock-only pending-challenge store.
 * @description Unlike the real backend, this mock has no server process of its own to hold
 * challenge state in memory — it's just browser JS. It persists pending challenges to localStorage
 * (a dedicated key, unrelated to this app's own auth storage) so a page refresh mid-2FA doesn't
 * lose the challenge, the same way the real backend's server-side challenge survives a client
 * reload. Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

const STORAGE_KEY = 'brownbox.mock.identity.challenges'
const DEMO_OTP_CODE = '123456'
const CHALLENGE_TTL_SECONDS = 600
const RESEND_COOLDOWN_SECONDS = 30

/** Which verify endpoint may redeem a challenge — login MFA or account activation. */
export type MockChallengePurpose = 'LOGIN' | 'ACTIVATION'

export interface MockChallenge {
  challengeToken: string
  /** Absent on a challenge persisted before this field existed — treat that as LOGIN. */
  purpose?: MockChallengePurpose
  userId: string
  code: string
  expiresAt: number
  resendAvailableAt: number
  failedAttempts: number
  resendCount: number
}

/**
 * @description Reads every pending challenge from storage.
 * @returns {Record<string, MockChallenge>} Challenges keyed by their token.
 */
function readAll(): Record<string, MockChallenge> {
  const raw = localStorage.getItem(STORAGE_KEY)

  if (!raw) {
    return {}
  }

  try {
    return JSON.parse(raw) as Record<string, MockChallenge>
  } catch {
    return {}
  }
}

/**
 * @description Persists the full challenge map.
 * @param {Record<string, MockChallenge>} challenges - Every pending challenge, keyed by token.
 */
function writeAll(challenges: Record<string, MockChallenge>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(challenges))
}

/**
 * @description Opens a fresh challenge for a user, replacing storage's full set of pending
 * challenges with just this one, mirroring the real backend allowing only one open challenge per
 * login attempt.
 * @param {string} userId - The mock user this challenge is for.
 * @param {MockChallengePurpose} [purpose] - Which verify endpoint may redeem it (default LOGIN).
 * @returns {MockChallenge} The newly opened challenge.
 */
export function createChallenge(
  userId: string,
  purpose: MockChallengePurpose = 'LOGIN',
): MockChallenge {
  const now = Date.now()
  const challenge: MockChallenge = {
    challengeToken: `mock-challenge-token.${userId}.${now}`,
    purpose,
    userId,
    code: DEMO_OTP_CODE,
    expiresAt: now + CHALLENGE_TTL_SECONDS * 1000,
    resendAvailableAt: now + RESEND_COOLDOWN_SECONDS * 1000,
    failedAttempts: 0,
    resendCount: 0,
  }

  writeAll({ [challenge.challengeToken]: challenge })
  return challenge
}

/**
 * @description Looks up a pending challenge by its token.
 * @param {string} challengeToken - The token to look up.
 * @returns {MockChallenge | null} The matching challenge, or null when there isn't one.
 */
export function getChallenge(challengeToken: string): MockChallenge | null {
  return readAll()[challengeToken] ?? null
}

/**
 * @description Issues a fresh code for an existing challenge, rotating its token and extending its
 * expiry/resend-cooldown, mirroring the real backend's resend behavior. Carries the resend count
 * forward across the rotation (createChallenge itself always starts a fresh challenge at 0), since
 * it tracks resends across the challenge's whole lifetime, not just its current token.
 * @param {string} challengeToken - The challenge to rotate.
 * @returns {MockChallenge | null} The rotated challenge, or null when the original wasn't found.
 */
export function rotateChallenge(challengeToken: string): MockChallenge | null {
  const existing = getChallenge(challengeToken)

  if (!existing) {
    return null
  }

  const rotated: MockChallenge = {
    ...createChallenge(existing.userId, existing.purpose ?? 'LOGIN'),
    resendCount: existing.resendCount + 1,
  }

  writeAll({ [rotated.challengeToken]: rotated })
  return rotated
}

/**
 * @description Removes a challenge once it's been verified (or abandoned).
 * @param {string} challengeToken - The challenge to remove.
 */
export function deleteChallenge(challengeToken: string): void {
  const all = readAll()
  delete all[challengeToken]
  writeAll(all)
}

/**
 * @description Records one more incorrect verification attempt against a challenge, mirroring the
 * real backend's max-attempts tracking (see MAX_FAILED_ATTEMPTS in two-factor-mock.service.ts,
 * the only caller).
 * @param {string} challengeToken - The challenge that was verified incorrectly.
 * @returns {MockChallenge | null} The updated challenge, or null when it wasn't found.
 */
export function incrementFailedAttempts(challengeToken: string): MockChallenge | null {
  const all = readAll()
  const existing = all[challengeToken]

  if (!existing) {
    return null
  }

  const updated: MockChallenge = { ...existing, failedAttempts: existing.failedAttempts + 1 }
  all[challengeToken] = updated
  writeAll(all)
  return updated
}
